import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 30px;
  height: fit-content;
  color: #2c3e50;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 15px;
    border-radius: 12px;
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #e74c3c;
  font-weight: 700;
  
  svg {
    font-size: 1.2em;
  }
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }
`;

const PlayersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  
  @media (max-width: 768px) {
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const PlayerItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 18px;
  border-radius: 15px;
  background: ${props => props.isCurrent ? '#fff3cd' : '#f8f9fa'};
  transition: all 0.3s ease;
  position: relative;
  border: 2px solid ${props => props.isCurrent ? '#f39c12' : '#e9ecef'};
  color: #2c3e50;
  
  ${props => props.isYou && `
    border-color: #e74c3c;
    box-shadow: 0 4px 15px rgba(231, 76, 60, 0.2);
  `}
  
  ${props => !props.isConnected && `
    opacity: 0.6;
    background: #ecf0f1;
  `}
  
  ${props => props.isCurrent && `
    box-shadow: 0 6px 20px rgba(243, 156, 18, 0.3);
    transform: scale(1.02);
  `}
  
  &:hover {
    background: ${props => props.isCurrent ? '#ffeeba' : '#ffffff'};
    border-color: ${props => props.isCurrent ? '#f39c12' : '#bdc3c7'};
  }
  
  @media (max-width: 768px) {
    padding: 15px;
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    gap: 10px;
    border-radius: 12px;
  }
`;

const Avatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${props => props.bg || 'linear-gradient(45deg, #ff6b6b, #4ecdc4)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  font-weight: bold;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  
  ${props => !props.isConnected && `
    filter: grayscale(80%);
  `}
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    font-size: 20px;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }
`;

const ConnectionStatus = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.isConnected ? '#4cd964' : '#FF3B30'};
  border: 2px solid white;
`;

const PlayerInfo = styled.div`
  flex: 1;
  overflow: hidden;
`;

const PlayerName = styled.div`
  font-weight: bold;
  font-size: 18px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  color: #2c3e50;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
  
  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 3px;
  }
`;

const PlayerBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  
  @media (max-width: 480px) {
    gap: 3px;
  }
`;

const YouBadge = styled.span`
  background: #e74c3c;
  color: #ffffff;
  font-size: 0.8rem;
  padding: 4px 10px;
  border-radius: 20px;
  margin-left: 10px;
  font-weight: bold;
`;

const HostBadge = styled.span`
  background: #27ae60;
  color: white;
  font-size: 0.8rem;
  padding: 4px 10px;
  border-radius: 20px;
  margin-left: 10px;
  font-weight: bold;
`;

const OfflineBadge = styled.span`
  background: #FF3B30;
  color: white;
  font-size: 0.7rem;
  padding: 3px 8px;
  border-radius: 20px;
  margin-left: 10px;
  opacity: 0.8;
`;

const JoiningBadge = styled.span`
  background: #FF9500;
  color: white;
  font-size: 0.7rem;
  padding: 3px 8px;
  border-radius: 20px;
  margin-left: 10px;
  animation: pulse 1.5s infinite;
  
  @keyframes pulse {
    0% {
      opacity: 0.7;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.7;
    }
  }
`;

const JoiningSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #FF9500;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-left: 5px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const TurnIndicator = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.isCurrent ? '#4ecdc4' : 'transparent'};
  box-shadow: ${props => props.isCurrent ? '0 0 10px #4ecdc4' : 'none'};
`;

const TurnBadge = styled.span`
  background-color: #f39c12;
  color: white;
  font-size: 0.8rem;
  padding: 4px 10px;
  border-radius: 20px;
  margin-left: 10px;
  font-weight: bold;
  animation: pulse 1.5s infinite;
  
  @keyframes pulse {
    0% {
      opacity: 0.8;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.8;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px 0;
  color: #7f8c8d;
  
  p {
    margin-top: 10px;
    font-size: 16px;
    font-weight: 500;
  }
`;

const PlayerList = ({ players, currentPlayerIndex, currentPlayerId, hostPlayerId, joiningPlayers = [] }) => {
  const { t } = useLanguage();
  const getInitials = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };
  
  const getRandomColor = (name) => {
    const colors = [
      'linear-gradient(45deg, #FF6B6B, #FF8E8E)',
      'linear-gradient(45deg, #4ECDC4, #79E2D7)',
      'linear-gradient(45deg, #FFD166, #FFE29D)',
      'linear-gradient(45deg, #6A0572, #AB83A1)',
      'linear-gradient(45deg, #5E72EB, #8E98F5)',
      'linear-gradient(45deg, #9D50BB, #BC78D7)',
      'linear-gradient(45deg, #F8B739, #FCCE84)',
      'linear-gradient(45deg, #52D726, #8FE968)',
      'linear-gradient(45deg, #FF0080, #FF6EB4)',
      'linear-gradient(45deg, #7E57C2, #A08CDA)'
    ];
    
    if (!name) return colors[0];
    
    // Use a hash of the name to get a consistent color
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Helper to get player name (either player_name or name property)
  const getPlayerName = (player) => {
    return player.player_name || player.name || 'Unknown Player';
  };
  
  // Sort players: current player first, then connected players, then disconnected
  const sortedPlayers = [...players].sort((a, b) => {
    // Current player's turn first
    if (players.indexOf(a) === currentPlayerIndex && players.indexOf(b) !== currentPlayerIndex) return -1;
    if (players.indexOf(a) !== currentPlayerIndex && players.indexOf(b) === currentPlayerIndex) return 1;
    
    // Then sort by connection status
    if (a.is_connected && !b.is_connected) return -1;
    if (!a.is_connected && b.is_connected) return 1;
    
    // Then sort by host status
    if (a.id === hostPlayerId && b.id !== hostPlayerId) return -1;
    if (a.id !== hostPlayerId && b.id === hostPlayerId) return 1;
    
    // Then sort by player name
    return getPlayerName(a).localeCompare(getPlayerName(b));
  });

  // Helper to check if a player is recently offline
  const isRecentlyOffline = (player) => {
    if (player.is_connected) return false;
    
    if (!player.last_active) return true;
    
    const lastActive = new Date(player.last_active);
    const now = new Date();
    const diffMinutes = (now - lastActive) / (1000 * 60);
    
    // Consider as recently offline if disconnected less than 2 minutes ago
    return diffMinutes < 2;
  };

  return (
    <Container>
      <Title>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
        {t('players')} ({players.length})
        <span style={{
          fontSize: '0.7rem',
          color: '#95a5a6',
          fontWeight: '500',
          marginLeft: 'auto',
          letterSpacing: '0.3px'
        }}>
          © 2024 Parteezy
        </span>
      </Title>
      
      {(players.length > 0 || joiningPlayers.length > 0) ? (
        <PlayersList>
          {sortedPlayers.map((player, index) => {
            const playerName = getPlayerName(player);
            const isCurrentPlayer = players.indexOf(player) === currentPlayerIndex;
            const isConnected = player.is_connected !== false;
            const wasRecentlyActive = isRecentlyOffline(player);
            
            return (
              <PlayerItem 
                key={player.id}
                isCurrent={isCurrentPlayer}
                isYou={player.id === currentPlayerId}
                isConnected={isConnected}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: isConnected ? 1 : 0.7, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Avatar 
                  bg={getRandomColor(playerName)}
                  isConnected={isConnected}
                >
                  {player.avatar_url ? (
                    <img 
                      src={player.avatar_url} 
                      alt={`${playerName}'s avatar`}
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <span style={{ display: player.avatar_url ? 'none' : 'block' }}>
                    {getInitials(playerName)}
                  </span>
                  <ConnectionStatus isConnected={isConnected} />
                </Avatar>
                <PlayerInfo>
                  <PlayerName>
                    {playerName}
                  </PlayerName>
                  <PlayerBadges>
                    {player.id === currentPlayerId && <YouBadge>{t('you')}</YouBadge>}
                    {player.id === hostPlayerId && <HostBadge>{t('host')}</HostBadge>}
                    {isCurrentPlayer && <TurnBadge>{t('currentTurnBadge')}</TurnBadge>}
                    {!isConnected && (
                      wasRecentlyActive ? 
                        <OfflineBadge style={{ backgroundColor: 'orange' }}>{t('away')}</OfflineBadge> : 
                        <OfflineBadge>{t('offline')}</OfflineBadge>
                    )}
                  </PlayerBadges>
                </PlayerInfo>
                <TurnIndicator isCurrent={isCurrentPlayer} />
              </PlayerItem>
            );
          })}
          
          {/* Show joining players */}
          {joiningPlayers.map((joiningPlayer, index) => (
            <PlayerItem 
              key={`joining-${joiningPlayer.name || index}`}
              isCurrent={false}
              isYou={false}
              isConnected={true}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 0.8, y: 0 }}
              transition={{ delay: (sortedPlayers.length + index) * 0.1 }}
            >
              <Avatar 
                bg={getRandomColor(joiningPlayer.name)}
                isConnected={true}
              >
                {joiningPlayer.avatar ? (
                  <img 
                    src={joiningPlayer.avatar} 
                    alt={`${joiningPlayer.name}'s avatar`}
                    onError={(e) => {
                      // Fallback to initials if image fails to load
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                ) : null}
                <span style={{ display: joiningPlayer.avatar ? 'none' : 'block' }}>
                  {getInitials(joiningPlayer.name)}
                </span>
                <JoiningSpinner />
              </Avatar>
              <PlayerInfo>
                <PlayerName>
                  {joiningPlayer.name}
                </PlayerName>
                <PlayerBadges>
                  <JoiningBadge>{t('joiningBadge')}</JoiningBadge>
                </PlayerBadges>
              </PlayerInfo>
            </PlayerItem>
          ))}
        </PlayersList>
      ) : (
        <EmptyState>
          <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7, margin: '0 auto', display: 'block' }}>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          <p>{t('noPlayersYet')}</p>
        </EmptyState>
      )}
    </Container>
  );
};

export default PlayerList; 